import React, { useEffect, useRef, useState } from 'react'
import { ChaseFileType, ChaseInputType, InterfaceChaseItem } from '../../types/chase-item-types'
import { providerPermissionFields } from '../providerComponents/providersData'
import { optionsData } from '../../data';
import CustomInput from '../CustomInput';
import { toast } from 'react-toastify';
import { ParseErrors } from '../../utils/ParseErrors';
import axiosInstance from '../../utils/axiosInstance';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpFromBracket, faCircleCheck, faCircleNotch, faCloudArrowDown, faEllipsisVertical, faPaperclip, faTrash } from '@fortawesome/free-solid-svg-icons';
import clsx from 'clsx';
import { Menu } from '@headlessui/react';
import UploadFile from '../../utils/UploadFile';
import { useDeleteModel } from '../../store/use-delete-model';
import DeleteModel from '../../models/delete-model';
import { ChaseInputTypeData } from './chaseListData';
import CustomButton from '../CustomButton';
import CompareObject from '../../utils/CompareObjects';
import Loader from '../Loader';
import { useMessageModel } from '../../store/use-message-model';
import { useChaseMenu } from '../../store/use-chase-menu';
import ChaseItemStatusSelect from './chase-item-status-select';
import DropdownPanel from '../DropdownPanel';


interface IChaseItem {
    item: InterfaceChaseItem | null
    UpdateItemData: () => void
}

interface IChaseLoadingState {
    status: boolean,
    files: boolean
    deleteFile: boolean
    inputs: boolean
}

/** Which file a download/open is running for, so only that chip shows a spinner. */
type BusyFile = { key: string; action: "download" | "open" } | null

const getObjectLabel = (item: string): string => {
    return ChaseInputTypeData.find((chasedata) => chasedata.id === item)?.label
}

const ChaseItem: React.FC<IChaseItem> = ({ item: itemData, UpdateItemData }) => {
    const [item,] = useState<InterfaceChaseItem | null>(itemData)
    const { onOpenDeletePopup, onCloseDeletePopup, initialValues } = useDeleteModel()
    const { initialValues: chaseInitialValues } = useChaseMenu()
    const { onOpen } = useMessageModel()
    const [isLoading, setIsLoading] = useState<IChaseLoadingState>({ status: false, files: false, deleteFile: false, inputs: false })
    const [busyFile, setBusyFile] = useState<BusyFile>(null)
    const providerDetails = providerPermissionFields.find((prov) => prov.id === item?.type)
    const [selectedOption, setSelectedOption] = useState(optionsData.find((optionData) => optionData.value === item?.status));
    const [chaseFiles, setChaseFiles] = useState<ChaseFileType[]>(item?.data[providerDetails.id])
    const [chaseInputs, setChaseInputs] = useState<ChaseInputType>(item?.data[item?.type][0])
    const [notes, setNotes] = useState<string>("")
    const menuButtonRef = useRef<HTMLButtonElement | null>(null)

    // ----------------------------------- [ UPDATE CHASE DATA ] ---------------------------------

    const UpdateChaseItem = async ({ newFiles, newInputs, requestType, isDelete = false }: { newFiles?: ChaseFileType[], newInputs?: any[], requestType?: string, isDelete?: boolean }) => {
        const type = requestType === "driversLicenseSummary" ? "inputs" : "files"
        try {
            await axiosInstance.patch(`/chase/item/update/${item.id}`, {
                data: {
                    [item.type]: type === "files" ? newFiles : newInputs,
                },
                isDelete
            })
            UpdateItemData()
            toast.success("Updated Successfully")
        } catch (error) {
            console.log(error)
            toast.error(ParseErrors(error))
        }
    }

    // ------------------------------------- [ HANDLE UPLOAD FILES ] ----------------------------------

    const HandleUploadFiles = async (e: React.ChangeEvent<HTMLInputElement>) => {
        setIsLoading((prev) => ({ ...prev, files: true }))
        try {
            let fileUploadPromise: Promise<{}>[] = []

            for (let fileIdx in e.target.files) {
                const file = e.target.files[fileIdx]

                if (file.name && file.type) {
                    fileUploadPromise.push(new Promise(async (resolve, reject) => {
                        try {
                            const fileResponse = await UploadFile({ file })
                            resolve(fileResponse)
                        } catch (error) {
                            reject(error)
                        }
                    }))
                }
            }

            const filesPromiseResponse: any = await Promise.all(fileUploadPromise)
            const formatedFilesData = filesPromiseResponse.map((fileRes) => fileRes?.status === 200 && fileRes?.data)
            await UpdateChaseItem({ newFiles: [...chaseFiles, ...formatedFilesData] })
            setIsLoading((prev) => ({ ...prev, files: false }))
        } catch (error) {
            console.log(error)
            toast.error(ParseErrors(error))
            setIsLoading((prev) => ({ ...prev, files: false }))
        }
    }


    // -------------------------------------- [ UPDATE CHASE ITEM STATUS ] ----------------------------

    const handleUpdateStatus = async (value) => {
        setIsLoading((prev) => ({ ...prev, status: true }))
        try {
            const res = await axiosInstance.patch(`/chase/item/update/status/${item.id}`, {
                status: value?.value,
                notes: notes
            })
            toast.success(res.data?.message)
            setSelectedOption(value);
            UpdateItemData()
            console.log(item?.chaseId)
            if (value?.value === "Rejected") {
                let rejectedMessage = ("💡Please ensure you message the customer with a reason for the rejection.")
                onOpen(chaseInitialValues?.id?.toString(), chaseInitialValues.data.firstName, rejectedMessage)
            }
            setIsLoading((prev) => ({ ...prev, status: false }))
        } catch (error) {
            console.log(error)
            toast.error(ParseErrors(error))
            setIsLoading((prev) => ({ ...prev, status: false }))
        }
    };


    // --------------------------------------- [ HANDLE DELETE FILE ] ----------------------------

    const HandleDeleteFile = async (key: string) => {
        setIsLoading((prev) => ({ ...prev, deleteFile: true }))
        onCloseDeletePopup()
        try {
            const filteredFiles = chaseFiles.filter((chaseFile) => chaseFile.fileLink !== key)
            const defaultValues = providerDetails.id === "driversLicenseSummary" ? [{ ...item?.data["driversLicenseSummary"][0] }, ...filteredFiles] : filteredFiles
            await UpdateChaseItem({ newFiles: defaultValues, isDelete: true })
            UpdateItemData()
            setIsLoading((prev) => ({ ...prev, deleteFile: false }))
        } catch (error) {
            toast.error(ParseErrors(error))
            setIsLoading((prev) => ({ ...prev, deleteFile: false }))
        }
    }


    // ----------------------------------------- [ HANDLE DELETE CHASE ITEMS ] -----------------------------
    const HandleDeleteChaseItem = async () => {
        setIsLoading((prev) => ({ ...prev, status: true }))
        onCloseDeletePopup()
        try {
            const chaseRes = await axiosInstance.delete(`/chase/item/delete/${item?.id}`)
            setIsLoading((prev) => ({ ...prev, status: false }))
            UpdateItemData()
            toast.success(chaseRes.data.message)
        } catch (error) {
            toast.error(ParseErrors(error))
            setIsLoading((prev) => ({ ...prev, status: false }))
        }
    }

    // ----------------------------------------- [ HANDLE UPDATE CHASE ITEM INPUTS ] -----------------------------

    const HandleUpdateChaseInputs = async () => {
        setIsLoading((prev) => ({ ...prev, inputs: true }))
        try {
            const defaultValues = [{ ...item?.data["driversLicenseSummary"][0], ...chaseInputs, type: "inputs" }, ...item?.data["driversLicenseSummary"].filter((item) => item?.type === "files")]
            await UpdateChaseItem({ requestType: "driversLicenseSummary", newInputs: defaultValues })
            UpdateItemData()
            setIsLoading((prev) => ({ ...prev, inputs: false }))
        } catch (error) {
            toast.error(ParseErrors(error))
            setIsLoading((prev) => ({ ...prev, inputs: false }))
        }
    }

    // -------------------------------- [ DOWNLOAD FILES ] --------------------------------

    const HandleDownLoadFile = async (fileName: string) => {
        setBusyFile({ key: fileName, action: "download" })
        try {
            const getFileLink = await axiosInstance.get(`/file/download/${fileName}`, {
                responseType: 'blob',
            })
            const urlFile = URL.createObjectURL(getFileLink.data);

            if (urlFile) {
                const a = document.createElement('a');
                document.body.appendChild(a);
                a.style.display = 'none';

                a.href = urlFile;
                a.download = fileName; // Set the extracted file name as the download attribute
                a.click();
            }
            setBusyFile(null)
        } catch (error) {
            toast.error(ParseErrors(error))
            setBusyFile(null)
            console.log(error)
        }
    }


    const HandleViewFile = async (fileName: string) => {
        setBusyFile({ key: fileName, action: "open" })
        try {
            const getFileLink = await axiosInstance.get(`/file/${fileName}`)
            window.open(getFileLink.data.url)
            setBusyFile(null)
        } catch (error) {
            toast.error(ParseErrors(error))
            setBusyFile(null)
        }
    }


    // ---------------------------- [ CHANGE CHASE INPUTS ] --------------------------------

    const onChangeChaseInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        setChaseInputs((prev) => ({ ...prev, [e.target.name]: e.target.value }))
    }

    useEffect(() => {
        setChaseFiles(itemData?.data[providerDetails.id])
        setSelectedOption(optionsData.find((optionData) => optionData.value === itemData?.status))
    }, [itemData, providerDetails.id])

    // ------------------------- [ UPLOADED FILES + UPLOAD BUTTON ] --------------------------

    const renderFiles = () => (
        <div className="flex items-center flex-wrap gap-3">
            {isLoading.files && (
                <div className="h-10 inline-flex items-center gap-2.5 px-4 rounded-full bg-surface-muted border border-edge-faint">
                    <FontAwesomeIcon icon={faCircleNotch} className="text-[13px] fa-spin text-blue" />
                    <p className="text-[13px] font-medium text-blue">Uploading files</p>
                </div>
            )}

            {chaseFiles?.map((chaseFileItem: ChaseFileType, index) => {
                const isOpening = busyFile?.key === chaseFileItem?.fileLink && busyFile?.action === "open"
                const isDownloading = busyFile?.key === chaseFileItem?.fileLink && busyFile?.action === "download"

                return (
                    <React.Fragment key={chaseFileItem.fileLink + "" + index}>
                        {chaseFileItem?.fileLink ? (
                            <div className="h-10 inline-flex items-center gap-2 pl-3.5 pr-1.5 rounded-full bg-surface-muted border border-edge-faint hover:border-indigo-400/40 transition-all duration-200">
                                <FontAwesomeIcon icon={faPaperclip} className="text-[12px] text-light-gray" />
                                <p
                                    className={clsx("cursor-pointer text-[13px] font-medium text-blue hover:text-accent-bright max-w-[150px] mobile:max-w-[110px] truncate capitalize", isOpening && "pointer-events-none opacity-70")}
                                    onClick={() => HandleViewFile(chaseFileItem?.fileLink)}
                                >
                                    {isOpening ? "Opening..." : chaseFileItem?.name}
                                </p>

                                <button
                                    type="button"
                                    title="Download file"
                                    disabled={isDownloading}
                                    className="h-7 w-7 rounded-full flex items-center justify-center text-light-gray hover:text-blue hover:bg-active-background disabled:cursor-wait transition-colors duration-200"
                                    onClick={() => HandleDownLoadFile(chaseFileItem?.fileLink)}
                                >
                                    <FontAwesomeIcon
                                        icon={isDownloading ? faCircleNotch : faCloudArrowDown}
                                        className={clsx("text-[13px]", isDownloading && "fa-spin text-blue")}
                                    />
                                </button>

                                <button
                                    type="button"
                                    title="Delete file"
                                    className="h-7 w-7 rounded-full flex items-center justify-center text-light-gray hover:text-rose-600 dark:hover:text-rose-300 hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors duration-200"
                                    onClick={() => onOpenDeletePopup({
                                        id: item?.id,
                                        onClick: () => HandleDeleteFile(chaseFileItem.fileLink),
                                        scope: "chase-file",
                                        title: "Delete this file?",
                                        description: `The file is removed from "${providerDetails.label}". The customer can upload a replacement.`,
                                        detail: chaseFileItem?.name,
                                        confirmLabel: "Delete file",
                                    })}
                                >
                                    <FontAwesomeIcon icon={faTrash} className="text-[12px]" />
                                </button>
                            </div>
                        ) : null}
                    </React.Fragment>
                )
            })}

            <label
                htmlFor={`chaseDoc-${item?.id}`}
                className="h-10 inline-flex items-center gap-2.5 px-4 rounded-full border border-dashed border-edge-strong text-[13px] font-semibold text-blue hover:text-accent-bright hover:border-indigo-400/40 hover:bg-overlay-hover transition-all duration-200 cursor-pointer relative"
            >
                <FontAwesomeIcon icon={faArrowUpFromBracket} className="text-[12px]" />
                Upload file
                <input
                    disabled={isLoading.files || isLoading.status}
                    type="file"
                    onChange={HandleUploadFiles}
                    id={`chaseDoc-${item?.id}`}
                    multiple
                    className="h-0 w-0 opacity-0 absolute top-0 left-0"
                />
            </label>
        </div>
    )

    // ----------------------------- [ APPROVAL NOTE FIELD ] --------------------------------

    const renderNotes = () => (
        <>
            {itemData?.status === "Pending-Review" ? (
                <div className="w-full mt-5">
                    <CustomInput
                        placeholder="Send note upon approval (optional)"
                        className="!text-[13px]"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                    />
                </div>
            ) : null}

            {itemData?.status === "Approved" && notes.length ? (
                <div className="mt-5">
                    <p className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-semibold bg-emerald-50 dark:bg-emerald-500/15 text-emerald-700 dark:text-emerald-300">
                        <FontAwesomeIcon icon={faCircleCheck} className="text-[12px]" />
                        Note sent to provider
                    </p>
                </div>
            ) : null}
        </>
    )

    //  -------------------------- [ SET NULL IF NO ITEM ] -----------------------------------
    if (!item) {
        return
    }


    return (
        <>
            <div className="w-full mt-4 bg-surface rounded-2xl border border-edge p-5 sm:p-6 smallTablet:!p-4 animate-fade-in">

                {/* --------------------------------- [ CHASE ITEMS HEADER ] ------------------------------- */}
                <div className="flex items-start gap-4 mb-6 smallTablet:flex-wrap smallTablet:gap-3 smallTablet:mb-4">
                    <div className="min-w-0">
                        <h1 className="text-[15px] font-bold text-text-primary">
                            {providerDetails.label}
                        </h1>
                        <p className='mt-1 text-[13px] font-medium text-light-gray'>{providerDetails.desc}</p>
                    </div>
                    <div className="ml-auto flex gap-2 items-center justify-end shrink-0 smallTablet:ml-0">
                        <div className="shrink-0">
                            <ChaseItemStatusSelect
                                options={optionsData}
                                value={selectedOption}
                                onChange={handleUpdateStatus}
                                disabled={isLoading.files || isLoading.inputs}
                                loading={isLoading?.status}
                            />
                        </div>
                        <Menu as="div" className="relative inline-block text-left">
                            {({ open }) => (
                                <>
                                    <Menu.Button
                                        ref={menuButtonRef}
                                        title="More actions"
                                        className="cursor-pointer h-8 w-8 rounded-lg flex items-center justify-center text-light-gray hover:text-text-primary hover:bg-overlay transition-colors duration-200"
                                    >
                                        <FontAwesomeIcon icon={faEllipsisVertical} className="text-[15px]" />
                                    </Menu.Button>

                                    <DropdownPanel open={open} anchorRef={menuButtonRef} align="right">
                                        <Menu.Items static className="min-w-[170px] p-1 rounded-xl bg-surface-elevated border border-edge shadow-overlay focus:outline-none">
                                            <Menu.Item>
                                                <div
                                                    className="flex gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium text-rose-600 dark:text-rose-300 items-center cursor-pointer hover:bg-rose-50 dark:hover:bg-rose-500/10 transition-colors duration-200"
                                                    onClick={() => onOpenDeletePopup({
                                                        id: item?.id,
                                                        onClick: HandleDeleteChaseItem,
                                                        scope: "chase-item",
                                                        title: "Delete this chase item?",
                                                        description: "The request and every document uploaded against it are removed from the chase. This can't be undone.",
                                                        detail: providerDetails.label,
                                                        confirmLabel: "Delete item",
                                                    })}
                                                >
                                                    <FontAwesomeIcon icon={faTrash} className="text-[12px]" />
                                                    Delete item
                                                </div>
                                            </Menu.Item>
                                        </Menu.Items>
                                    </DropdownPanel>
                                </>
                            )}
                        </Menu>
                    </div>
                </div>
                {/* --------------------------------- [ CHASE ITEMS HEADER ] ------------------------------- */}


                {providerDetails.id === "driversLicenseSummary" ?
                    <>
                        {/* ----------------------------- [ CHASE INPUT TYPE] -------------------------- */}
                        <div className='flex items-center flex-wrap justify-start gap-3'>
                            {Object.keys(item.data[providerDetails.id][0])?.reverse()?.map((chaseInputItem: string, index: number) => {

                                if (chaseInputItem === "type") {
                                    return null
                                }

                                return (
                                    <div className='w-[220px] smallTablet:w-full' key={item?.id + index}>
                                        <CustomInput placeholder={`${getObjectLabel(chaseInputItem)}`} name={chaseInputItem} className='!text-[13px] !rounded-full !px-4' defaultValue={chaseInputs[chaseInputItem] || item.data[providerDetails.id][0][chaseInputItem]} onChange={onChangeChaseInput} />
                                    </div>
                                )
                            })}

                            {CompareObject({ oldValues: item.data[providerDetails.id][0], newValues: chaseInputs }) ?
                                <CustomButton variant="primary" className='!h-10 px-5 !rounded-full !text-[13px] font-semibold cursor-pointer' onClick={HandleUpdateChaseInputs} disabled={isLoading.files || isLoading.status || !chaseInputs.driversLicenceNumber || !chaseInputs.nationalInsuranceNumber || !chaseInputs.postCode}>
                                    {isLoading.inputs ? <Loader /> : "Update"}
                                </CustomButton> : null}
                        </div>
                        {/* ----------------------------- [ CHASE INPUT TYPE] -------------------------- */}

                        <div className='mt-5'>
                            {renderFiles()}
                        </div>

                        {renderNotes()}


                    </> : <>
                        {renderFiles()}

                        {renderNotes()}

                    </>
                }

                {/* --------------------------------- [ MESSAGE ] ------------------------------------- */}
            </div>
            {initialValues.id === item?.id && (initialValues.scope === "chase-item" || initialValues.scope === "chase-file") && <DeleteModel loading={isLoading?.deleteFile || isLoading?.status} />}
        </>

    )
}

export default ChaseItem
